import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, BadgeDollarSign, Building2, Calendar, ClipboardList, FileText, Hash, MapPin, User } from "lucide-react";

import ApprovalWorkflowManager from "@/components/approvals/approval-workflow-manager";
import InlineEditableField from "@/components/inline-editable-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrencyWithSymbol } from "@/helpers/currency-formatter";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { ApprovalWorkflowConfig, BreadcrumbItem, Company, Sector } from "@/types";
import { formatDateAR } from "@/utils/date";

interface SectorShowProps {
  sector: Sector;
  approval_workflow: ApprovalWorkflowConfig | null;
  companies: Company[];
}

export default function SectorShow({ sector, approval_workflow, companies }: SectorShowProps) {
  const routes = useOrganizationRoutes();
  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    { title: "Sectores", href: routes.sectors.index() },
    { title: sector.name, href: "#" },
  ];

  const formatValue = (v: string | number | boolean | null | undefined) => {
    if (!v || typeof v !== 'string') return 'No especificado';
    return formatDateAR(v);
  };

  const formatCurrency = (v: string | number | boolean | null | undefined) => {
    if (!v || (typeof v !== 'number' && typeof v !== 'string')) return 'No especificado';
    return formatCurrencyWithSymbol(v, 'ARS');
  };

  const companyOptions = companies.map((company) => ({
    label: company.name ?? company.legal_name ?? `Compañía ${company.id}`,
    value: company.id,
  }));

  // Busca en companyOptions (no solo en sector.company) porque al editar inline
  // el usuario puede seleccionar una compañía distinta y necesitamos mostrar
  // su label antes de que el formulario se envíe y la página se refresque.
  const formatCompanyValue = (
    v: string | number | boolean | null | undefined,
  ) => {
    if (!v) return "Sin compañía";

    const found = companyOptions.find((option) => String(option.value) === String(v));
    if (found) return found.label;

    if (sector.company?.name || sector.company?.legal_name) {
      return sector.company.name ?? sector.company.legal_name ?? "Sin compañía";
    }

    return "Sin compañía";
  };

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head title={`Sector: ${sector.name}`} />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link href={routes.sectors.index()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Sectores
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">{sector.name}</h1>
            <p className="text-muted-foreground">Gestiona la información del sector/obra</p>
          </div>
        </div>

        {/* General */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Información General
            </CardTitle>
            <CardDescription>Haz clic en el lápiz para editar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="Nombre"
                value={sector.name}
                field="name"
                updateUrl={routes.sectors.show(sector)}
                icon={FileText}
                placeholder="Nombre del sector"
              />

              <InlineEditableField
                label="Código"
                value={sector.code}
                field="code"
                updateUrl={routes.sectors.show(sector)}
                icon={Hash}
                placeholder="Código interno"
              />

              <InlineEditableField
                label="Compañía"
                value={sector.company_id ?? ""}
                field="company_id"
                type="select"
                options={companyOptions}
                updateUrl={routes.sectors.show(sector)}
                icon={Building2}
                placeholder="Sin compañía"
                formatValue={formatCompanyValue}
              />

              <InlineEditableField
                label="Cliente"
                value={sector.client}
                field="client"
                updateUrl={routes.sectors.show(sector)}
                icon={Building2}
                placeholder="Cliente o razón social"
              />

              <InlineEditableField
                label="Unidad Operativa"
                value={sector.operational_unit}
                field="operational_unit"
                updateUrl={routes.sectors.show(sector)}
                icon={ClipboardList}
                placeholder="Unidad operativa"
              />

              <InlineEditableField
                label="Jefe de Obra"
                value={sector.project_manager}
                field="project_manager"
                updateUrl={routes.sectors.show(sector)}
                icon={User}
                placeholder="Responsable/jefe de obra"
              />

              <InlineEditableField
                label="Tipo de Obra"
                value={sector.work_type}
                field="work_type"
                updateUrl={routes.sectors.show(sector)}
                icon={ClipboardList}
                placeholder="Tipo de obra"
              />

              <InlineEditableField
                label="Número de Obra"
                value={sector.work_number ?? undefined}
                field="work_number"
                type="number"
                updateUrl={routes.sectors.show(sector)}
                icon={Hash}
                placeholder="Número de obra"
              />

              <InlineEditableField
                label="Dirección de la Obra"
                value={sector.address}
                field="address"
                updateUrl={routes.sectors.show(sector)}
                icon={MapPin}
                placeholder="Dirección de la obra"
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6">
              <InlineEditableField
                label="Descripción"
                value={sector.description}
                field="description"
                type="textarea"
                updateUrl={routes.sectors.show(sector)}
                icon={FileText}
                placeholder="Agregar descripción de la obra"
              />
            </div>
          </CardContent>
        </Card>

        {/* Fechas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Fechas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="Inicio"
                value={sector.start_date}
                field="start_date"
                type="date"
                updateUrl={routes.sectors.show(sector)}
                icon={Calendar}
                formatValue={formatValue}
              />
              <InlineEditableField
                label="Entrega"
                value={sector.delivery_date}
                field="delivery_date"
                type="date"
                updateUrl={routes.sectors.show(sector)}
                icon={Calendar}
                formatValue={formatValue}
              />
              <InlineEditableField
                label="Finalización"
                value={sector.end_date}
                field="end_date"
                type="date"
                updateUrl={routes.sectors.show(sector)}
                icon={Calendar}
                formatValue={formatValue}
              />
            </div>
          </CardContent>
        </Card>

        {/* Estado y Valor */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BadgeDollarSign className="h-5 w-5" />
              Estado y Valor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InlineEditableField
                label="Activa"
                value={!!sector.active}
                field="active"
                type="boolean"
                updateUrl={routes.sectors.show(sector)}
                icon={ClipboardList}
              />

              <InlineEditableField
                label="Valor de Obra"
                value={sector.project_value ?? undefined}
                field="project_value"
                type="number"
                updateUrl={routes.sectors.show(sector)}
                icon={BadgeDollarSign}
                placeholder="0,00"
                formatValue={formatCurrency}
              />
            </div>
          </CardContent>
        </Card>

        <ApprovalWorkflowManager
          sectorId={sector.id}
          workflow={approval_workflow}
        />
      </div>
    </AppLayout>
  );
}
